// Third Party Imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';

import Modal from '@homeaway/react-modal';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';

class DeferLoading extends Component {
    static propTypes = {
        importComponent: PropTypes.func.isRequired,
        importFunc: PropTypes.func.isRequired,
        importModule: PropTypes.string,
        placeholderHeight: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        placeholderWidth: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
    };

    static defaultProps = {
        importModule: 'default',
        placeholderHeight: 0,
        placeholderWidth: 0,
    };

    constructor(props) {
        super(props);
        this._isMounted = false;

        this.state = {
            loaded: false,
            component: () => null,
        };
    }

    componentDidMount() {
        this._isMounted = true;

        const {importFunc, importModule} = this.props;

        importFunc().then((result) => {
            this.setStateSafe({component: result[importModule], loaded: true});
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    setStateSafe = (newState) => {
        // Defend against error: Can't call setState (or forceUpdate) on an unmounted component.
        if (this._isMounted) {
            this.setState(newState);
        }
    };

    recursivelyReturnChildren = (children) => {
        return React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            if (child.props.children) {
                child = React.cloneElement(child, {
                    children: this.recursivelyReturnChildren(
                        child.props.children
                    ),
                });
            }

            // if child being rendered matches the importComponent prop, replace the null component with the imported one
            if (
                child.type.toString() === this.props.importComponent.toString()
            ) {
                return <this.state.component {...child.props} />;
            }

            // otherwise return the child itself
            return child;
        });
    };

    setDimensions = (
        placeholderWidth,
        placeholderHeight,
        blockPlaceholder,
        placeholderStyle
    ) => {
        if (blockPlaceholder) {
            placeholderStyle.height = placeholderHeight
                ? `${placeholderHeight}px`
                : 'auto';
            placeholderStyle.width = placeholderWidth
                ? `${placeholderWidth}px`
                : 'auto';
        }
    };

    getPlaceholderValues = (placeholderWidth, placeholderHeight) => {
        const fullscreenPlaceholder = placeholderHeight === 'fullscreen';
        const blockPlaceholder =
            (!!placeholderHeight || !!placeholderWidth) &&
            !fullscreenPlaceholder;
        const placeholderStyle = {};
        this.setDimensions(
            placeholderWidth,
            placeholderHeight,
            blockPlaceholder,
            placeholderStyle
        );
        return {blockPlaceholder, placeholderStyle, fullscreenPlaceholder};
    };

    renderLoadingOverlay = (blockPlaceholder, placeholderStyle) => {
        const {loaded} = this.state;
        return (
            !loaded &&
            blockPlaceholder && (
                <div
                    className="defer-loading__placeholder"
                    style={placeholderStyle}
                >
                    <LoadingOverlay />
                </div>
            )
        );
    };

    renderModal = (fullscreenPlaceholder) => {
        const {loaded} = this.state;
        return (
            !loaded &&
            fullscreenPlaceholder && (
                <Modal
                    id="defer-loading-fullscreen-placeholder"
                    isOpen
                    header={false}
                    isLoading
                />
            )
        );
    };

    render() {
        const {children, placeholderHeight, placeholderWidth} = this.props;
        const {loaded} = this.state;
        const {placeholderStyle, blockPlaceholder, fullscreenPlaceholder} =
            this.getPlaceholderValues(placeholderWidth, placeholderHeight);

        return (
            <React.Fragment>
                {loaded && this.recursivelyReturnChildren(children)}
                {this.renderLoadingOverlay(blockPlaceholder, placeholderStyle)}
                {this.renderModal(fullscreenPlaceholder)}
            </React.Fragment>
        );
    }
}

export default DeferLoading;
